import { Observable, Subject } from 'rxjs';

import { Injectable } from '@angular/core';

import { SnSessionService } from '@lib/auth';
import { FsDataDogRumService } from '@lib/data-dog-rum';

import { AblyClient } from './ably.client';

interface MessageMetrics {
  messageCount: number;
  startTime: number;
  messages: Array<{
    id: string;
    receivedTimestamp: number;
    channel: string;
  }>;
}

const DEFAULT_METRIC_INTERVAL = 120000; // 2 Minutes

@Injectable({ providedIn: 'root' })
export class SnAblyService {
  private _ablyClient: AblyClient;
  private readonly _loaded$ = new Subject<void>();
  private _organizationId: string | null = null;
  private _messageMetrics: MessageMetrics = {
    messageCount: 0,
    startTime: Date.now(),
    messages: [],
  };
  private _metricsTimer: any = null;

  constructor(
    private readonly _sessionService: SnSessionService,
    private readonly _datadogService: FsDataDogRumService,
  ) {}

  get ablyClient(): AblyClient {
    return this._ablyClient;
  }

  get loaded$(): Observable<void> {
    return this._loaded$.asObservable();
  }

  get isAblyAvailable(): boolean {
    return !!localStorage.getItem('ablyToken');
  }

  public initAblyClient(token: string, organizationId?: string): void {
    this._organizationId = organizationId || null;

    if (token) {
      this._ablyClient = new AblyClient(token, (tokenParams, callback) => {
        this._sessionService.getAblyToken().subscribe((res) => {
          callback(null, res.token);
          this._loaded$.next();
        });
      });

      this._resetMessageMetrics();
      this._startMessageMetricsTracking();
    }
  }

  public destroyAblyClient(): void {
    this._sendMessageMetrics();
    if (this._metricsTimer) {
      clearInterval(this._metricsTimer);
      this._metricsTimer = null;
    }
    this._ablyClient?.destroy();
  }

  public trackMessage(message: any, channel: string): void {
    this._messageMetrics.messageCount++;
    this._messageMetrics.messages.push({
      id: message.id || message.data?.id || 'unknown',
      receivedTimestamp: Date.now(),
      channel,
    });
  }

  private _resetMessageMetrics(): void {
    this._messageMetrics = {
      messageCount: 0,
      startTime: Date.now(),
      messages: [],
    };
  }

  private _startMessageMetricsTracking(intervalMs: number = DEFAULT_METRIC_INTERVAL): void {
    this._resetMessageMetrics();
    this._metricsTimer = setInterval(() => {
      this._sendMessageMetrics();
    }, intervalMs);
  }

  private _sendMessageMetrics(): void {
    const { messageCount, startTime, messages } = this._messageMetrics;
    const duration = Date.now() - startTime;

    if (messageCount > 0) {
      this._datadogService.addAction('ably_message_metrics', {
        messageCount,
        duration,
        organizationId: this._organizationId || 'unknown',
        messages,
      });
      this._resetMessageMetrics();
    }
  }
}
